<template>
  <BottomDrawer
    :isOpen="modelValue"
    @close="handleClose"
  >
    <div class="carriage-drawer">
      <div class="carriage-drawer__header">
        <h1>Delstrekning</h1>
        <h2>{{ departureStation }} - {{ arrivalStation }}</h2>
      </div>

      <div class="carriage-drawer__content">
        <CarriageLayout
          ref="layoutRef"
          :serviceName="serviceName"
          :serviceIdentifier="serviceIdentifier"
          :fromStation="fromStation"
          :toStation="toStation"
          :travelDate="travelDate"
          :bookingNumber="bookingNumber"
          :passengerInfo="passengerInfo"
          :selectedSeats="convertedSeats"
          :currentSegment="currentSegmentObject"
          :legIndex="legIndex"
          :seatPrices="seatPrices"
          :carriageLayout="carriageLayout"
          @seatSelected="handleSeatSelected"
        />
      </div>

      <div class="carriage-drawer__actions">
        <Button
          class="carriage-drawer__button"
          theme="secondary"
          @click="handleClose"
        >
          Avbryt
        </Button>
        <Button
          class="carriage-drawer__button"
          @click="handleSave"
        >
          Lagre valg
        </Button>
      </div>
    </div>
  </BottomDrawer>
</template>

<script lang="ts">
import { defineComponent, ref, PropType, computed } from 'vue'
import BottomDrawer from '@/components/BaseComponents/BottomDrawer.vue'
import CarriageLayout from './CarriageLayout.vue'
import Button from '@/components/BaseComponents/Button.vue'
import type { CarriageSeat } from '@/types/seatTypes'
import type { PassengerInfo } from '@/types/bookingTypes'

export default defineComponent({
  name: 'CarriageDrawer',
  components: {
    BottomDrawer,
    CarriageLayout,
    Button
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    departureStation: {
      type: String,
      required: true
    },
    arrivalStation: {
      type: String,
      required: true
    },
    serviceName: {
      type: String,
      required: true
    },
    serviceIdentifier: {
      type: String,
      required: true
    },
    fromStation: {
      type: String,
      required: true
    },
    toStation: {
      type: String,
      required: true
    },
    travelDate: {
      type: String,
      required: true
    },
    bookingNumber: {
      type: String,
      required: true
    },
    passengerInfo: {
      type: Array as PropType<PassengerInfo[]>,
      required: true
    },
    selectedSeats: {
      type: Array as PropType<CarriageSeat[]>,
      required: true
    },
    currentSegment: {
      type: String as PropType<'outbound' | 'inbound'>,
      required: true
    },
    legIndex: {
      type: Number,
      required: true
    },
    seatPrices: {
      type: Object as PropType<{ [key: number]: number }>,
      required: true,
      default: () => ({})
    },
    carriageLayout: {
      type: Object as PropType<any>,
      required: true
    }
  },
  emits: ['update:modelValue', 'close', 'save', 'seatSelected'],
  setup(props, { emit }) {
    const layoutRef = ref()
    const convertedSeats = computed(() => props.selectedSeats || [])

    const currentSegmentObject = computed(() => ({
      direction: props.currentSegment,
      serviceIdentifier: props.serviceIdentifier,
      fromStation: props.fromStation,
      toStation: props.toStation,
      travelDate: props.travelDate,
      serviceName: props.serviceName,
      segmentId: `${props.currentSegment}_${props.legIndex}`
    }))

    const handleClose = () => {
      emit('update:modelValue', false)
      emit('close')
    }

    const handleSave = () => {
      emit('save')
      handleClose()
    }

    const handleSeatSelected = (data: { seat: CarriageSeat; legIndex: number; direction: any }) => {
      // Extract the actual direction string from the direction object if needed
      const direction = typeof data.direction === 'object' ? data.direction.direction : data.direction
      emit('seatSelected', {
        seat: data.seat,
        legIndex: data.legIndex,
        direction: direction
      })
    }

    return {
      layoutRef,
      convertedSeats,
      currentSegmentObject,
      handleClose,
      handleSave,
      handleSeatSelected
    }
  }
})
</script>

<style lang="scss" scoped>
.carriage-drawer {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__header {
    margin-bottom: 1rem;
    text-align: center;

    h1 {
      @extend .title-medium;
      margin-bottom: 0.5rem;
    }

    h2 {
      @extend .body-large;
      color: var(--color-text-secondary);
    }
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    margin: -1rem;
    padding: 1rem;
  }

  &__actions {
    display: flex;
    gap: 16px;
    margin-top: 1rem;
    margin-right: -16px;
    margin-left: -16px;
    padding: rem(16) 16px;
    border-top: 1px solid var(--color-border);
  }

  &__button {
    flex: 1;
  }
}
</style>
